/**
 * TagEditor — attach/detach reusable tags on ANY entity (location, block, plant_zone,
 * plant, irrigation_zone). Self-contained: loads tags + assignments via react-query,
 * shows the entity's DIRECT tags as removable chips, and an inline add (pick an
 * existing tag or type a new name to create it). Inheritance (a container's tag
 * applying to its contents) is computed by the consuming view, not here.
 *
 * Drop in anywhere: <TagEditor entityType="block" entityId={b.id} />
 */
import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tagsApi, TAG_COLORS, type Tag, type TagAssignment, type TagEntityType } from '../../lib/api'

// Shared hook so a page can also read tags/assignments for filtering + inheritance.
export function useTags() {
  const tags = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list().then(r => r.data.tags) })
  const assignments = useQuery({ queryKey: ['tag-assignments'], queryFn: () => tagsApi.assignments().then(r => r.data.assignments) })
  return {
    tags: (tags.data ?? []) as Tag[],
    assignments: (assignments.data ?? []) as TagAssignment[],
    loading: tags.isLoading || assignments.isLoading,
  }
}

export function TagEditor({ entityType, entityId, label }: { entityType: TagEntityType; entityId: string; label?: string }) {
  const qc = useQueryClient()
  const { tags, assignments } = useTags()
  const [input, setInput] = useState('')
  const [adding, setAdding] = useState(false)

  const tagById = useMemo(() => new Map(tags.map(t => [t.id, t])), [tags])
  const mine = assignments.filter(a => a.entity_type === entityType && a.entity_id === entityId)
  const myIds = new Set(mine.map(a => a.tag_id))
  const myTags = mine.map(a => tagById.get(a.tag_id)).filter(Boolean) as Tag[]

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tags'] })
    qc.invalidateQueries({ queryKey: ['tag-assignments'] })
  }
  const assign = useMutation({
    mutationFn: (p: { tag_id?: string; name?: string; color?: string }) => tagsApi.assign({ ...p, entity_type: entityType, entity_id: entityId }),
    onSuccess: () => { invalidate(); setInput(''); setAdding(false) },
  })
  const unassign = useMutation({
    mutationFn: (tag_id: string) => tagsApi.unassign({ tag_id, entity_type: entityType, entity_id: entityId }),
    onSuccess: invalidate,
  })

  const addByName = (name: string) => {
    const n = name.trim()
    if (!n) return
    const existing = tags.find(t => t.name.toLowerCase() === n.toLowerCase())
    if (existing) { if (!myIds.has(existing.id)) assign.mutate({ tag_id: existing.id }) }
    else assign.mutate({ name: n, color: TAG_COLORS[tags.length % TAG_COLORS.length] })
  }
  const suggestions = tags.filter(t => !myIds.has(t.id) && (!input || t.name.toLowerCase().includes(input.toLowerCase()))).slice(0, 8)

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
      {label && <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginRight: 2 }}>{label}</span>}
      {myTags.map(t => (
        <span key={t.id} style={chip(t.color)}>
          {t.name}
          <button onClick={() => unassign.mutate(t.id)} aria-label={`Remove ${t.name}`} title="Remove tag"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.color, padding: 0, marginLeft: 4, fontSize: 12, lineHeight: 1 }}>×</button>
        </span>
      ))}
      {adding ? (
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <input
            autoFocus
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { addByName(input) } if (e.key === 'Escape') { setInput(''); setAdding(false) } }}
            onBlur={() => setTimeout(() => setAdding(false), 150)}
            placeholder="tag…"
            style={{ width: 110, fontSize: 11, padding: '2px 6px', borderRadius: 100, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', outline: 'none' }}
          />
          {suggestions.length > 0 && (
            <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 50, background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 4, minWidth: 130, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
              {suggestions.map(t => (
                <button key={t.id} onMouseDown={() => assign.mutate({ tag_id: t.id })}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: 11, color: 'var(--color-text)', borderRadius: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 8, background: t.color }} />{t.name}
                </button>
              ))}
              {input.trim() && !tags.some(t => t.name.toLowerCase() === input.trim().toLowerCase()) && (
                <button onMouseDown={() => addByName(input)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: 11, color: 'var(--color-primary)', borderRadius: 6 }}>
                  + Create “{input.trim()}”
                </button>
              )}
            </div>
          )}
        </span>
      ) : (
        <button onClick={() => setAdding(true)} title="Add a tag"
          style={{ fontSize: 11, color: 'var(--color-text-muted)', background: 'none', border: '1px dashed var(--color-border)', borderRadius: 100, padding: '2px 8px', cursor: 'pointer' }}>
          + tag
        </button>
      )}
    </div>
  )
}

const chip = (color: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600, color,
  background: `color-mix(in srgb, ${color} 16%, transparent)`, border: `1px solid color-mix(in srgb, ${color} 45%, transparent)`,
  borderRadius: 100, padding: '1px 4px 1px 8px',
})

export default TagEditor
